import {
  StreamParams as KmmStreamParams,
  StreamUrlNetworkModel
} from '@norlys-a-s/norlys-play-kmm';
import { NextEpisode, StreamType } from '@smart-tv/typings/content';
import { Stream } from '@smart-tv/components/player/types';
import { networkError } from '@smart-tv/contexts/error-context/error-context';
import getUnixDate from '@smart-tv/utils/date/get-unix-date';
import isAfter from '@smart-tv/utils/date/is-after';
import { retryAsync } from '@smart-tv/utils/retry';
import { storePersistentData } from '@smart-tv/utils/persistent-data/persistent-data';

import { checkNetwork } from './check-network';
import { kmm } from './kmm';

interface GetStreamUrlParams {
  sid?: number;
  eventId?: number;
  mediaId?: number;
  progress?: number;
}

const getStreamType = (data: StreamUrlNetworkModel, params: GetStreamUrlParams): StreamType => {
  const { sid, eventId, mediaId } = params;

  if (mediaId) {
    return StreamType.Vod;
  }

  const endTime = data.epgData?.endTimeEpochSeconds;

  if (sid && eventId && endTime && isAfter(getUnixDate(endTime), Date.now())) {
    return StreamType.StartOver;
  }

  if (sid && eventId) {
    return StreamType.CatchUp;
  }

  return StreamType.LiveStream;
};

const toKmmStreamParams = (params: GetStreamUrlParams): KmmStreamParams => {
  const { sid, eventId, mediaId, progress } = params;
  const getCurrentPlayerPositionInSeconds = () => progress ?? 0;

  if (mediaId) {
    return new KmmStreamParams.OnDemandStream(
      mediaId,
      false,
      false,
      null,
      null,
      null,
      getCurrentPlayerPositionInSeconds
    );
  }

  if (sid && eventId) {
    return new KmmStreamParams.ChannelEventStream(
      sid,
      eventId,
      false,
      null,
      null,
      null,
      getCurrentPlayerPositionInSeconds
    );
  }

  return new KmmStreamParams.ChannelLiveStream(sid ?? 0, null, null, null);
};

const responseMapper = (data: StreamUrlNetworkModel, params: GetStreamUrlParams): Stream => {
  const next = data.nextAsset;

  return {
    drmEngine: data.drmEngine ?? '',
    streamUrl: data.streamUrl ?? '',
    streamType: getStreamType(data, params),
    startPoint: data.startPointSeconds ?? undefined,
    trickmodes: data.trickmodes?.bitmask ?? 0,
    fastForwardLimit: data.maxFastForwardPointSeconds ?? Number.MAX_VALUE,
    licenseServerUrl: data.licenseServerUrl ?? undefined,
    licenseCertificateUrl: data.licenseCertificateUrl ?? undefined,
    nextEpisode: next
      ? new NextEpisode(
          next.mediaId ?? undefined,
          next.sid ?? undefined,
          next.eventId ?? undefined,
          next.showNextButtonAt ?? undefined,
          next.numberOfSeasons ?? undefined,
          next.numberOfEpisodes ?? undefined,
          next.seriesTitle ?? undefined,
          next.episodeNumber ?? undefined,
          next.seasonNumber ?? undefined,
          next.title ?? undefined,
          next.shortDescription ?? undefined,
          next.longDescription ?? undefined,
          next.trackingCategory ?? undefined
        )
      : undefined,
    duration: data.durationSeconds ?? undefined,
    subtitle: data.subtitle ?? '',
    title: data.title ?? '',
    keepAliveIntervalInSecs: data.keepAliveIntervalInSeconds ?? 0,
    updateStartoverNextInSecs: data.updateStartOverNextInSeconds
  };
};

export const getStreamUrl = async (params: GetStreamUrlParams): Promise<Stream> => {
  const isLiveOrStartOver = params.sid && !params.mediaId;
  if (isLiveOrStartOver) {
    const hasNetwork = await checkNetwork();
    if (!hasNetwork) {
      throw networkError;
    }
  }

  const streamParams = toKmmStreamParams(params);

  const data = await retryAsync(() => kmm((repo) => repo.api.getStreamUrl(streamParams), false), {
    maxRetries: 3,
    baseDelayMs: 200
  });

  storePersistentData('active-stream-set', { timestamp: Date.now(), params });

  return responseMapper(data, params);
};

export default getStreamUrl;
